import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useAppDispatch,
  useAppNavigate,
  useAppParams,
  useAppSelector,
  useAppState,
  useAppTranslation,
} from "../../../constants/hooks/global-hooks";
import { ErrorModal, HorizontalLinearStepper } from "../../../components";
import { RootState } from "../../../store";
import { getMessageConstantMessageCountAction } from "../../../actions/message-constant";
import {
  createMessageAction,
  getMessageAction,
  modifyMessageAction,
} from "../../../actions/message";
import {
  resetMessage,
  setMessageId,
  setMessageTitle,
} from "../../../constants/reducers/message.reducer";
import { MessageMutationForm } from "./components";

import "./mutate-message.page.scss";
import { QueryKeys } from "../../../constants/query-keys";

export function MutateMessage() {
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const param = useAppParams();
  const navigate = useAppNavigate();
  const { id } = useAppSelector((state: RootState) => state.languages);

  const [state, setState] = useAppState({
    modalVisible: false,
    backwards: false,
  });

  const getContant = useQuery({
    queryKey: [QueryKeys.messageQuery],
    queryFn: () => getMessageConstantMessageCountAction(parseInt(param.id!)),
  });

  const { mutate, error } = useMutation({
    mutationFn: (params: { languageId: number; constantId: number }) =>
      createMessageAction(params),
    onSuccess() {
      queryClient.invalidateQueries([QueryKeys.messageConstantQuery]);
      dispatch(resetMessage());
      setState((prevState) => ({ ...prevState, backwards: false }));
    },
    onError: () => {
      setState((prevState) => ({ ...prevState, modalVisible: true }));
    },
  });

  const messageQuery = useMutation({
    mutationFn: (params: { languageId: number; constantId: number }) =>
      getMessageAction(params),
    onSuccess(data) {
      dispatch(setMessageId(data.id));
      dispatch(setMessageTitle(data.message));
      setState((prevState) => ({ ...prevState, backwards: true }));
    },
    onError: () => {
      setState((prevState) => ({ ...prevState, modalVisible: true }));
    },
  });

  const modifyMessage = useMutation({
    mutationFn: modifyMessageAction,
    onSuccess() {
      queryClient.invalidateQueries([QueryKeys.messageQuery]);
      dispatch(resetMessage());
      setState((prevState) => ({ ...prevState, backwards: false }));
    },
    onError: () => {
      setState((prevState) => ({ ...prevState, modalVisible: true }));
    },
  });

  const languageData: any = queryClient.getQueryData([QueryKeys.languageQuery]);
  const handleSave = () => {
    if (state.backwards === true) {
      modifyMessage.mutate();
    } else {
      const params = {
        languageId: id ? id : languageData?.languages[0].id,
        constantId: parseInt(param.id!),
      };
      mutate(params);
    }
  };

  const handleFinish = () => {
    const params = {
      languageId: id,
      constantId: parseInt(param.id!),
    };
    mutate(params);
    navigate("/cms/messages");
  };

  const handleClose = () => {
    setState((prevState) => ({ ...prevState, modalVisible: false }));
  };

  return (
    <main className="page mutate-message-page">
      <h1>{t("mutate-message-page-title")}</h1>
      <div className="data-creation-container">
        {!getContant.isLoading && (
          <>
            <div>
              <MessageMutationForm />
            </div>
            <div className="stepper">
              <HorizontalLinearStepper
                onNextClick={handleSave}
                onFinish={handleFinish}
                onPrevClick={(languageId: number) =>
                  messageQuery.mutate({
                    languageId,
                    constantId: parseInt(param.id!),
                  })
                }
                stepSkips={getContant.data}
              />
            </div>
          </>
        )}
      </div>

      {state.modalVisible && (
        <ErrorModal
          error={error ?? messageQuery.error}
          handleClose={handleClose}
        />
      )}
    </main>
  );
}
